// Request bucket page (Refund / No Charge / Take Back / Bad Tow / …)
//
// Mirrors Call History (dispatch/pages/CallHistoryPage.js):
//   - 4 stat cards at top reacting to the active filter set
//   - Date preset row (Today / Last 7 Days / This Month / Custom)
//   - Full filter row: property name, region, status, submitted by,
//     processed by, search. Auto-runs on every change (no Search button).
//   - Export Excel button (top right)
//
// Bucket-specific extras preserved:
//   - bad_tow: deduction total + investigation outcome column
//   - has_amount buckets: Amount column

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { formatMoney } from '../../utils/money';
import { formatDate as fmtDateCT } from '../../utils/dateUtils';
import { useIsMobile, MobilePage, StickyAction, BottomSheet, Icon, Dialog } from '../../mobile';

const STATUS_COLORS = {
  submitted: '#3b82f6',
  processing: '#f59e0b',
  ready_for_payment: '#8b5cf6',
  completed: '#10b981',
  denied: '#ef4444',
  already_released: '#f59e0b',
  bid_sent: '#10b981',
  forwarded: '#10b981',
  approved: '#10b981',
  scheduled: '#8b5cf6',
  published: '#0891b2',
  cancelled: '#64748b',
};
const STATUS_LABELS = {
  submitted: 'Submitted',
  processing: 'Processing',
  ready_for_payment: 'Ready for Payment',
  completed: 'Completed',
  denied: 'Denied',
  already_released: 'Already Released',
  bid_sent: 'Bid Sent',
  forwarded: 'Forwarded to Titan',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'On Route',
  cancelled: 'Cancelled',
};
// Gate Access reads its steps, not generic request statuses.
const GATE_STATUS_LABELS = {
  submitted: 'Assign Rep',
  processing: 'With Rep',
  completed: 'Access Granted',
};
// The rep's closeout picks (requests.gate_access_type / gate_resolution) —
// mirrors gateAccessService.
const GATE_ACCESS_TYPES = { code: 'Gate code', app: 'App', clicker: 'Clicker / remote', toll_tag: 'Toll tag', other: 'Other' };
const GATE_RESOLUTIONS = { parkingsnap: 'Updated in ParkingSnap', driver: 'Provided to Driver' };

// Sign work dates are pure calendar dates (YYYY-MM-DD, no clock) — noon UTC
// keeps them stable in every browser timezone. Don't run them through
// fmtDateCT, which assumes UTC timestamps.
function fmtDateOnly(dateStr) {
  if (!dateStr) return '—';
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return fmtDateCT(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Central-time date helpers (match CallHistoryPage)
function dateToCT(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}
const getTodayCT = () => dateToCT(new Date());
const getLast7DaysCT = () => dateToCT(new Date(Date.now() - 7 * 86400000));
function getThisMonthStartCT() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  return `${y}-${m}-01`;
}

export default function RequestBucketPage() {
  // Scope badge for the header. The server tells us the actual scope via
  // bucket-stats.scope_kind ('all' | 'reports' | 'own') — we just render
  // the label. 'all' = admin company-wide, 'reports' = manager seeing
  // their team, 'own' = regular user (personal-only).
  const { user, can } = useAuth(); // eslint-disable-line no-unused-vars
  const { type } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);
  // Sign Requests and Tagging Requests are the same shape of bucket: a
  // scheduled field job with a work summary, a work date, a region and a
  // time estimate. Everything below that used to be `type === 'sign_request'`
  // now reads from this descriptor so the two stay in lockstep.
  const FIELD_JOB_BUCKETS = {
    sign_request: {
      summaryCol: 'sign_work_summary', regionCol: 'sign_region',
      schedulePath: '/sales/requests/sign_request/schedule', managerPosition: 'Sign Manager',
    },
    tagging_request: {
      summaryCol: 'tagging_work_summary', regionCol: 'tagging_region',
      schedulePath: '/sales/requests/tagging_request/schedule', managerPosition: 'Tagging Manager',
    },
  };
  const fieldJob = FIELD_JOB_BUCKETS[type] || null;
  const statusLabels = type === 'gate_access' ? { ...STATUS_LABELS, ...GATE_STATUS_LABELS } : STATUS_LABELS;
  const [bucket, setBucket] = useState(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showOutsourceRule, setShowOutsourceRule] = useState(false);
  const [error, setError] = useState('');

  const [activePreset, setActivePreset] = useState('month');
  const [filters, setFilters] = useState({
    property_name: '',
    region: '',
    status: '',
    submitted_by: '',
    processed_by: '',
    search: '',
    start_date: getThisMonthStartCT(),
    end_date: getTodayCT(),
  });

  // Bucket metadata + user list (one-time)
  useEffect(() => {
    api.getRequestTypes()
      .then(types => {
        const found = (types || []).find(t => t.key === type);
        if (!found) setError(`Unknown request type: ${type}`);
        setBucket(found || null);
      })
      .catch(err => setError(err.message || 'Failed to load bucket'));
    api.getActiveUsers().then(setActiveUsers).catch(() => {});
  }, [type]);

  const buildFilters = useCallback(() => {
    const out = { type };
    for (const k of Object.keys(filters)) {
      if (filters[k]) out[k] = filters[k];
    }
    return out;
  }, [filters, type]);

  const doSearch = useCallback(async (overrideFilters) => {
    setLoading(true);
    setError('');
    try {
      const f = overrideFilters || buildFilters();
      const [{ requests }, bucketStats] = await Promise.all([
        api.getRequests({ ...f, limit: 200 }),
        api.getRequestBucketStats(f).catch(() => null),
      ]);
      setRows(requests || []);
      setStats(bucketStats);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  // Auto-run pattern — LOCKED (feedback_history_page_pattern.md):
  // every filter change re-runs the fetch. Free-text search debounces ~300ms
  // so typing doesn't thrash the server. No Search button anywhere.
  useEffect(() => {
    if (!bucket) return;
    const handle = setTimeout(() => { doSearch(); }, 300);
    return () => clearTimeout(handle);
  }, [bucket, filters, doSearch]);

  function handlePreset(preset) {
    setActivePreset(preset);
    const newFilters = { ...filters };
    if (preset === 'today') {
      newFilters.start_date = getTodayCT(); newFilters.end_date = getTodayCT();
    } else if (preset === '7days') {
      newFilters.start_date = getLast7DaysCT(); newFilters.end_date = getTodayCT();
    } else if (preset === 'month') {
      newFilters.start_date = getThisMonthStartCT(); newFilters.end_date = getTodayCT();
    } else if (preset === 'custom') {
      newFilters.start_date = ''; newFilters.end_date = '';
    }
    setFilters(newFilters); // auto-effect picks it up + re-fetches
  }

  function clearFilters() {
    setFilters({
      property_name: '', region: '', status: '', submitted_by: '', processed_by: '', search: '',
      start_date: getThisMonthStartCT(), end_date: getTodayCT(),
    });
    setActivePreset('month');
  }

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams(buildFilters()).toString();
      const response = await fetch(`/api/requests/export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Export failed');
    }
    setExporting(false);
  }

  const hasActiveFilters = filters.property_name || filters.region || filters.status
    || filters.submitted_by || filters.processed_by || filters.search;

  // ── Stat cards ─────────────────────────────────────────────────
  // For has_amount buckets: Total / Submitted / Completed / $ Amount Total
  // For bad_tow:            Total / Pending / Approved / $ Deduction Total
  // For others:             Total / Submitted / Completed / Denied
  let statsCards = [];
  if (stats) {
    if (fieldJob) {
      // 'submitted' = emergencies / awaiting scheduling; 'scheduled' = date
      // assigned on the Schedule Board.
      statsCards = [
        { label: 'Total', value: stats.total || 0, color: '#0f172a' },
        { label: 'Scheduled', value: stats.by_status?.scheduled || 0, color: '#8b5cf6' },
        { label: 'Awaiting Scheduling', value: stats.by_status?.submitted || 0, color: '#dc2626' },
        { label: 'Completed', value: stats.by_status?.completed || 0, color: '#22c55e' },
      ];
    } else if (type === 'gate_access') {
      statsCards = [
        { label: 'Total', value: stats.total || 0, color: '#0f172a' },
        { label: 'Assign Rep', value: stats.by_status?.submitted || 0, color: '#f59e0b' },
        { label: 'With Rep', value: stats.by_status?.processing || 0, color: '#4f46e5' },
        { label: 'Access Granted', value: stats.by_status?.completed || 0, color: '#22c55e' },
      ];
    } else if (type === 'item_request') {
      // Items total comes from the filtered rows — the generic bucket-stats
      // endpoint doesn't aggregate item_count.
      const itemsTotal = rows.reduce((sum, r) => sum + (Number(r.item_count) || 0), 0);
      statsCards = [
        { label: 'Total', value: stats.total || 0, color: '#0f172a' },
        { label: 'Submitted', value: stats.by_status?.submitted || 0, color: '#3b82f6' },
        { label: 'Completed', value: stats.by_status?.completed || 0, color: '#22c55e' },
        { label: 'Items Requested', value: itemsTotal.toLocaleString('en-US'), color: '#0891b2' },
      ];
    } else if (type === 'bad_tow') {
      const approved = rows.filter(r => r.investigation_outcome === 'approved').length;
      const pending = rows.filter(r => !r.investigation_outcome).length;
      statsCards = [
        { label: 'Total', value: stats.total || 0, color: '#0f172a' },
        { label: 'Pending', value: pending, color: '#f59e0b' },
        { label: 'Approved', value: approved, color: '#22c55e' },
        { label: 'Deductions', value: formatMoney(stats.deduction_total), color: '#dc2626' },
      ];
    } else if (bucket?.has_amount) {
      statsCards = [
        { label: 'Total', value: stats.total || 0, color: '#0f172a' },
        { label: 'Submitted', value: stats.by_status?.submitted || 0, color: '#3b82f6' },
        { label: 'Completed', value: stats.by_status?.completed || 0, color: '#22c55e' },
        { label: 'Amount', value: formatMoney(stats.amount_total), color: '#6d28d9' },
      ];
    } else {
      statsCards = [
        { label: 'Total', value: stats.total || 0, color: '#0f172a' },
        { label: 'Submitted', value: stats.by_status?.submitted || 0, color: '#3b82f6' },
        { label: 'Completed', value: stats.by_status?.completed || 0, color: '#22c55e' },
        { label: 'Denied', value: stats.by_status?.denied || 0, color: '#ef4444' },
      ];
    }
  }

  if (!bucket && !error) return <p style={{ padding: 24, color: 'var(--text-muted)' }}>Loading…</p>;

  const submitPath = type === 'audit' ? '/sales/audits/new' : `/sales/requests/${type}/new`;
  const canSeeSchedule = fieldJob && (user?.permission_tier === 'super_admin' || user?.permission_tier === 'admin' || user?.position_name === fieldJob.managerPosition);

  // ── Phone: list of cards, presets as chips, filters in a sheet ─────
  // Submit is the one thing a rep does here, so it is pinned. Stats are
  // three numbers. Export and the long filter row live behind the
  // filter button. Each request is a card: account, what, when, status.
  if (isMobile) {
    const scopeLabel = stats?.scope_kind === 'all' ? 'Company-wide' : stats?.scope_kind === 'reports' ? 'My team' : 'My submissions';
    const secondaryLine = r => {
      if (type === 'item_request') {
        const itemType = r.item_type_other ? `Other: ${r.item_type_other}` : r.reason_name;
        return r.item_count != null ? `${Number(r.item_count).toLocaleString('en-US')} items${itemType ? ` · ${itemType}` : ''}` : (itemType || '');
      }
      if (fieldJob) return r[fieldJob.summaryCol] || '';
      if (type === 'gate_access') return [r.notes, r.gate_rep_name ? `Rep: ${r.gate_rep_name}` : null].filter(Boolean).join(' · ');
      return [r.vehicle_info, r.reason_name || r.reason].filter(Boolean).join(' · ');
    };
    const amountText = r => !bucket?.has_amount ? null
      : r.refund_category === 'full' ? 'Full' : r.refund_category === 'half' ? 'Half'
      : r.amount_requested ? formatMoney(r.amount_requested) : null;
    const whenText = r => fieldJob
      ? (r.scheduled_date ? fmtDateOnly(r.scheduled_date) : r.tagging_due_date ? `Due ${fmtDateOnly(r.tagging_due_date)}` : 'Awaiting date')
      : formatDate(r.created_at);
    const presets = [['today', 'Today'], ['7days', '7 days'], ['month', 'This month'], ['custom', 'Custom']];
    const filterCount = ['region', 'status', 'submitted_by', 'processed_by', 'search'].filter(k => filters[k]).length;
    return (
      <MobilePage title={bucket?.label || type} subtitle={`${scopeLabel}${stats ? ` · ${stats.total || 0} in range` : ''}`}
        search={filters.property_name} onSearch={v => setFilters({ ...filters, property_name: v })} searchPlaceholder="Property name"
        side={(
          <div className="m-title-actions">
            <button type="button" className="m-btn-icon sm" aria-label="Back" onClick={() => navigate('/sales/requests')}><Icon name="back" size={20} /></button>
            <button type="button" className="m-btn-icon sm" aria-label="Filters" onClick={() => setShowFilters(true)}>
              <Icon name="filter" size={20} />{filterCount > 0 && <span className="m-dot" />}
            </button>
          </div>
        )}>
        {statsCards.length > 0 && (
          <div className="m-stats">
            {statsCards.slice(0, 3).map(s => (
              <div key={s.label} className="m-stat" style={{ '--m-stat-color': s.color }}>
                <div className="m-stat-value" style={{ fontSize: String(s.value).length > 6 ? 16 : 22 }}>{s.value}</div>
                <div className="m-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}
        <div className="m-chips">
          {presets.map(([k, label]) => (
            <button key={k} type="button" className={`m-chip ${activePreset === k ? 'active' : ''}`} onClick={() => handlePreset(k)}>{label}</button>
          ))}
          {canSeeSchedule && (
            <button type="button" className="m-chip" onClick={() => navigate(fieldJob.schedulePath)} style={{ marginLeft: 'auto' }}>Schedule board</button>
          )}
        </div>
        {activePreset === 'custom' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input type="date" className="sales-input" value={filters.start_date} onChange={e => setFilters({ ...filters, start_date: e.target.value })} style={{ flex: 1, fontSize: 16, minHeight: 44 }} />
            <input type="date" className="sales-input" value={filters.end_date} onChange={e => setFilters({ ...filters, end_date: e.target.value })} style={{ flex: 1, fontSize: 16, minHeight: 44 }} />
          </div>
        )}
        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
        {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : rows.length === 0 ? (
          <div className="m-card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 28 }}>
            No {bucket?.label?.toLowerCase() || 'request'}s match.
            {hasActiveFilters ? <div><button type="button" className="m-chip" style={{ marginTop: 12 }} onClick={clearFilters}>Clear filters</button></div> : null}
          </div>
        ) : (
          <div className="m-list">
            {rows.map(r => {
              const sub = secondaryLine(r); const amt = amountText(r);
              return (
                <div key={r.id} className="m-card m-task" role="button" tabIndex={0} onClick={() => navigate(`/sales/requests/${type}/${r.id}`)}
                  onKeyDown={e => { if (e.key === 'Enter') navigate(`/sales/requests/${type}/${r.id}`); }}>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
                      <div className="m-task-title" style={{ WebkitLineClamp: 1 }}>{r.account_name || '—'}</div>
                      {amt && <div style={{ fontSize: 15, fontWeight: 800, flexShrink: 0 }}>{amt}</div>}
                      {type === 'bad_tow' && r.deduction_amount != null && <div style={{ fontSize: 15, fontWeight: 800, color: '#dc2626', flexShrink: 0 }}>{formatMoney(r.deduction_amount)}</div>}
                    </div>
                    {sub && <div className="m-task-desc">{sub}</div>}
                    <div className="m-task-pills">
                      <span className="m-pill" style={{ background: `${STATUS_COLORS[r.status]}15`, color: STATUS_COLORS[r.status] }}>{(statusLabels[r.status] || r.status).toUpperCase()}</span>
                      {r.is_emergency ? <span className="m-pill" style={{ background: '#fef2f2', color: '#dc2626' }}>EMERGENCY</span> : null}
                      {r.tagging_frequency && r.tagging_frequency !== 'single' ? <span className="m-pill" style={{ background: '#e0f2fe', color: '#075985' }}>{r.tagging_frequency === 'monthly' ? 'MONTHLY' : 'QUARTERLY'}</span> : null}
                      <span className="m-task-meta" style={{ alignSelf: 'center', color: fieldJob && !r.scheduled_date && !r.tagging_due_date ? '#dc2626' : undefined, fontWeight: fieldJob ? 700 : 500 }}>{whenText(r)}</span>
                      {type === 'bad_tow' && r.investigation_outcome ? <span className="m-task-meta" style={{ alignSelf: 'center', textTransform: 'capitalize' }}>{r.investigation_outcome}</span> : null}
                    </div>
                  </div>
                  <Icon name="chevron" size={20} color="#cbd5e1" />
                </div>
              );
            })}
          </div>
        )}

        {!bucket?.gm_only && (
          <StickyAction>
            <button type="button" className="m-btn m-btn-primary" onClick={() => navigate(submitPath)}>
              <Icon name="plus" size={22} color="#fff" /> Submit {bucket?.label || 'request'}
            </button>
          </StickyAction>
        )}

        <BottomSheet open={showFilters} title="Filters" subtitle={`${rows.length} shown`} onClose={() => setShowFilters(false)}
          footer={(
            <>
              <button type="button" className="m-btn m-btn-secondary" onClick={() => { clearFilters(); }}>Clear</button>
              <button type="button" className="m-btn m-btn-primary" onClick={() => setShowFilters(false)}>Done</button>
            </>
          )}>
          <label className="sales-label">Region</label>
          <select className="sales-input" value={filters.region} onChange={e => setFilters({ ...filters, region: e.target.value })} style={{ fontSize: 16, minHeight: 48 }}>
            <option value="">All regions</option><option>Fort Worth</option><option>Dallas</option><option>Denton</option>
          </select>
          <label className="sales-label">Status</label>
          <select className="sales-input" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} style={{ fontSize: 16, minHeight: 48 }}>
            <option value="">All statuses</option>
            {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="sales-label">Submitted by</label>
          <select className="sales-input" value={filters.submitted_by} onChange={e => setFilters({ ...filters, submitted_by: e.target.value })} style={{ fontSize: 16, minHeight: 48 }}>
            <option value="">Anyone</option>
            {activeUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <label className="sales-label">Processed by</label>
          <select className="sales-input" value={filters.processed_by} onChange={e => setFilters({ ...filters, processed_by: e.target.value })} style={{ fontSize: 16, minHeight: 48 }}>
            <option value="">Anyone</option>
            {activeUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <label className="sales-label">Search reason / vehicle / notes</label>
          <input className="sales-input" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} style={{ fontSize: 16, minHeight: 48 }} />
          <button type="button" className="m-btn m-btn-secondary" style={{ marginTop: 12 }} onClick={handleExport} disabled={exporting || rows.length === 0}>
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
          {user?.permission_tier === 'super_admin' && !['sign_request', 'tagging_request', 'gate_access', 'take_back'].includes(type) && (
            <button type="button" className="m-btn m-btn-secondary" onClick={() => { setShowFilters(false); setShowOutsourceRule(true); }}>Auto-Outsource settings</button>
          )}
        </BottomSheet>
        {showOutsourceRule && <OutsourceRuleModal type={type} bucketLabel={bucket?.label || type} onClose={() => setShowOutsourceRule(false)} />}
      </MobilePage>
    );
  }

  return (
    <div className="history-page">
      <button onClick={() => navigate('/sales/requests')}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
        ← Back to Requests
      </button>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, flexWrap: 'wrap' }}>
          {bucket?.icon && <span style={{ fontSize: 24 }}>{bucket.icon}</span>}
          <span>{bucket?.label || type}</span>
          {/* Scope badge — server-driven, three states:
              all       → admin sees company-wide  (navy, white text)
              reports   → manager sees self + team (purple)
              own       → regular user, personal   (blue) */}
          {stats?.scope_kind && (() => {
            const palette = stats.scope_kind === 'all'
              ? { bg: '#0f172a', fg: '#fff',     label: 'Scope: Company-wide' }
              : stats.scope_kind === 'reports'
              ? { bg: '#8b5cf615', fg: '#8b5cf6', label: "Scope: My Team" }
              : { bg: '#3b82f615', fg: '#3b82f6', label: 'Scope: My Submissions' };
            return (
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                padding: '4px 10px', borderRadius: 999,
                background: palette.bg, color: palette.fg,
                textTransform: 'uppercase',
              }}>
                {palette.label}
              </span>
            );
          })()}
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Customer-facing flyer downloads. Files live in client/public/flyers/
              and are hot-swappable — drop in a new PDF with the same filename. */}
          {(() => {
            const FLYER_BUTTONS_BY_BUCKET = {
              striping_bid: [
                { href: '/flyers/LineandShineFlyer.pdf', label: '⬇ Download Flyer' },
              ],
              valet_trash: [
                { href: '/flyers/TitanTrash_ValetTrashFlyer.pdf', label: '⬇ Valet Trash Flyer' },
                { href: '/flyers/TitanTrash_BulkTrashFlyer.pdf',  label: '⬇ Bulk Trash Flyer' },
              ],
            };
            const flyers = FLYER_BUTTONS_BY_BUCKET[type] || [];
            return flyers.map(f => (
              <a
                key={f.href}
                href={f.href}
                download
                className="btn"
                style={{
                  fontWeight: 700, textDecoration: 'none',
                  background: '#fff', color: bucket?.color || '#475569',
                  border: `2px solid ${bucket?.color || '#475569'}`,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {f.label}
              </a>
            ));
          })()}
          {fieldJob && (user?.permission_tier === 'super_admin' || user?.permission_tier === 'admin' || user?.position_name === fieldJob.managerPosition) && (
            <button className="btn btn-muted" onClick={() => navigate(fieldJob.schedulePath)}
              style={{ fontWeight: 700 }}>
              📋 Schedule Board
            </button>
          )}
          {/* Bucket-level auto-outsource: every new request in this bucket is
              emailed to an outside vendor with a Task Complete link. Not
              offered on buckets with their own completion machinery. */}
          {!['sign_request', 'tagging_request', 'gate_access', 'take_back'].includes(type)
            && user?.permission_tier === 'super_admin' && (
            <button className="btn" onClick={() => setShowOutsourceRule(true)} style={{
              fontWeight: 700, background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a',
            }}>
              📤 Auto-Outsource
            </button>
          )}
          {!bucket?.gm_only && (
            <button
              className="btn btn-gold"
              onClick={() => navigate(type === 'audit' ? '/sales/audits/new' : type === 'gate_access' ? '/gate-report' : `/sales/requests/${type}/new`)}
              style={{ fontWeight: 700 }}>
              + Submit new {bucket?.label || 'Request'}
            </button>
          )}
          <button type="button" className="btn btn-gold" onClick={handleExport}
            disabled={exporting || rows.length === 0}>
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
      </div>

      {/* Stat cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
          {statsCards.map(s => (
            <div key={s.label} style={{
              background: '#fff', borderRadius: 12, padding: '14px 16px',
              border: '1px solid #e2e8f0', textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters — mirrors Call History */}
      <div className="search-filters">
        <div className="date-preset-row">
          <button type="button" className={`btn btn-sm ${activePreset === 'today' ? 'btn-primary' : 'btn-muted'}`}
            onClick={() => handlePreset('today')}>Today</button>
          <button type="button" className={`btn btn-sm ${activePreset === '7days' ? 'btn-primary' : 'btn-muted'}`}
            onClick={() => handlePreset('7days')}>Last 7 Days</button>
          <button type="button" className={`btn btn-sm ${activePreset === 'month' ? 'btn-primary' : 'btn-muted'}`}
            onClick={() => handlePreset('month')}>This Month</button>
          <button type="button" className={`btn btn-sm ${activePreset === 'custom' ? 'btn-primary' : 'btn-muted'}`}
            onClick={() => handlePreset('custom')}>Custom</button>
        </div>
        {activePreset === 'custom' && (
          <div className="date-range-row">
            <label>From</label>
            <input type="date" value={filters.start_date}
              onChange={e => setFilters({ ...filters, start_date: e.target.value })} />
            <label>To</label>
            <input type="date" value={filters.end_date}
              onChange={e => setFilters({ ...filters, end_date: e.target.value })} />
          </div>
        )}
        <div className="filter-row">
          <input placeholder="Property name…" value={filters.property_name}
            onChange={e => setFilters({ ...filters, property_name: e.target.value })} />
          <select value={filters.region} onChange={e => setFilters({ ...filters, region: e.target.value })}>
            <option value="">All Regions</option>
            <option>Fort Worth</option><option>Dallas</option><option>Denton</option>
          </select>
          <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All Statuses</option>
            {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.submitted_by} onChange={e => setFilters({ ...filters, submitted_by: e.target.value })}>
            <option value="">Submitted By</option>
            {activeUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <select value={filters.processed_by} onChange={e => setFilters({ ...filters, processed_by: e.target.value })}>
            <option value="">Processed By</option>
            {activeUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <input placeholder="Search reason / vehicle / notes…" value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })} />
          {hasActiveFilters && (
            <button type="button" onClick={clearFilters}
              style={{ fontSize: 11, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
              Clear Filters
            </button>
          )}
          {loading && <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Loading…</span>}
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fee', border: '1px solid #c00', borderRadius: 8, color: '#900', marginBottom: 16, fontWeight: 600 }}>
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--text-muted)',
          background: '#fff', borderRadius: 8, border: '1px dashed var(--border-subtle)',
        }}>
          No {bucket?.label ? `${bucket.label.toLowerCase()}${/s$/i.test(bucket.label) ? '' : 's'}` : 'requests'} match these filters.
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                <th>Account</th>
                {type === 'item_request'
                  ? <th style={{ textAlign: 'right' }}>Items</th>
                  : type === 'gate_access'
                  ? <th>What's not working</th>
                  : fieldJob
                  ? <th>Work</th>
                  : <th className="hide-mobile">Vehicle</th>}
                {fieldJob
                  ? <th style={{ whiteSpace: 'nowrap' }}>Work Date</th>
                  : <th className="hide-mobile">{type === 'item_request' ? 'Item Type' : type === 'gate_access' ? 'Rep' : 'Reason'}</th>}
                {fieldJob ? <th className="hide-mobile">Region</th> : null}
                {fieldJob ? <th className="hide-mobile" style={{ textAlign: 'right' }}>Est.</th> : null}
                {bucket?.has_amount ? <th style={{ textAlign: 'right' }}>Amount</th> : null}
                {type === 'bad_tow' ? <th className="hide-mobile">Driver</th> : null}
                {type === 'bad_tow' ? <th style={{ textAlign: 'right' }}>Deduction</th> : null}
                {type === 'bad_tow' ? <th>Outcome</th> : null}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/requests/${type}/${r.id}`)}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#64748b' }}>
                    {formatDate(r.created_at)}
                    {r.is_emergency ? (
                      <div style={{
                        fontSize: 9, fontWeight: 800, color: '#fff', background: '#ef4444',
                        padding: '1px 6px', borderRadius: 4, marginTop: 2, display: 'inline-block',
                      }}>🚨 EMERGENCY</div>
                    ) : null}
                  </td>
                  <td style={{ fontWeight: 600 }}>{r.account_name || '—'}</td>
                  {type === 'item_request' ? (
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {r.item_count != null ? Number(r.item_count).toLocaleString('en-US') : '—'}
                    </td>
                  ) : type === 'gate_access' ? (
                    <td style={{ fontSize: 13, color: '#334155', maxWidth: 380 }}>
                      {r.notes ? (r.notes.length > 90 ? `${r.notes.slice(0, 90)}…` : r.notes) : '—'}
                    </td>
                  ) : fieldJob ? (
                    <td style={{ fontSize: 13, color: '#334155' }}>
                      {r[fieldJob.summaryCol] || '—'}
                      {r.tagging_frequency && r.tagging_frequency !== 'single' ? (
                        <span title={`Standing ${r.tagging_frequency} sweep — visit #${r.tagging_occurrence || 1}`}
                          style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#075985', background: '#e0f2fe', borderRadius: 5, padding: '1px 6px' }}>
                          🔁 {r.tagging_frequency === 'monthly' ? 'Monthly' : 'Quarterly'}
                        </span>
                      ) : null}
                    </td>
                  ) : (
                    <td className="hide-mobile" style={{ fontSize: 13, color: '#334155' }}>{r.vehicle_info || '—'}</td>
                  )}
                  {fieldJob ? (
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {r.scheduled_date
                        ? fmtDateOnly(r.scheduled_date)
                        : r.tagging_due_date
                          ? <span style={{ color: '#075985', fontSize: 12, fontWeight: 700 }} title="Recurring sweep — not due yet">Due {fmtDateOnly(r.tagging_due_date)}</span>
                          : <span style={{ color: '#dc2626', fontSize: 12, fontWeight: 700 }}>Awaiting</span>}
                    </td>
                  ) : (
                    <td className="hide-mobile" style={{ fontSize: 13, color: '#334155' }}>{type === 'gate_access'
                      ? (r.gate_rep_name || <span style={{ color: '#b45309', fontWeight: 600 }}>Unassigned</span>)
                      : r.item_type_other ? `Other: ${r.item_type_other}` : (r.reason_name || r.reason || '—')}</td>
                  )}
                  {fieldJob ? (
                    <td className="hide-mobile" style={{ fontSize: 13, color: '#334155' }}>{r[fieldJob.regionCol] || '—'}</td>
                  ) : null}
                  {fieldJob ? (
                    <td className="hide-mobile" style={{ textAlign: 'right', fontSize: 13, color: '#334155' }}>
                      {r.estimated_minutes
                        ? (r.estimated_minutes >= 60 ? `${r.estimated_minutes / 60}h` : `${r.estimated_minutes}m`)
                        : '—'}
                    </td>
                  ) : null}
                  {bucket?.has_amount ? (
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {r.refund_category === 'full' && 'Full'}
                      {r.refund_category === 'half' && 'Half'}
                      {r.refund_category === 'other' && (r.amount_requested ? formatMoney(r.amount_requested) : 'Other')}
                      {!r.refund_category && (r.amount_requested ? formatMoney(r.amount_requested) : '—')}
                    </td>
                  ) : null}
                  {type === 'bad_tow' ? (
                    <td className="hide-mobile" style={{ fontSize: 13 }}>{r.driver_name || '—'}</td>
                  ) : null}
                  {type === 'bad_tow' ? (
                    <td style={{ textAlign: 'right', fontWeight: 700, color: r.deduction_amount ? '#dc2626' : 'var(--text-muted)' }}>
                      {r.deduction_amount != null ? formatMoney(r.deduction_amount) : '—'}
                    </td>
                  ) : null}
                  {type === 'bad_tow' ? (
                    <td>
                      {r.investigation_outcome ? (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                          background: r.investigation_outcome === 'approved' ? '#10b98115'
                                     : r.investigation_outcome === 'denied' ? '#ef444415' : '#64748b15',
                          color:       r.investigation_outcome === 'approved' ? '#065f46'
                                     : r.investigation_outcome === 'denied' ? '#991b1b' : '#475569',
                          textTransform: 'uppercase',
                        }}>{r.investigation_outcome}</span>
                      ) : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Pending</span>}
                    </td>
                  ) : null}
                  <td>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                      background: `${STATUS_COLORS[r.status]}15`, color: STATUS_COLORS[r.status],
                      textTransform: 'uppercase',
                    }}>
                      {statusLabels[r.status] || r.status}
                    </span>
                    {r.gate_access_type ? (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                        {[GATE_ACCESS_TYPES[r.gate_access_type], GATE_RESOLUTIONS[r.gate_resolution]].filter(Boolean).join(' · ')}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showOutsourceRule && (
        <OutsourceRuleModal
          type={type}
          bucketLabel={bucket?.label || type}
          onClose={() => setShowOutsourceRule(false)}
        />
      )}
    </div>
  );
}

// ── Bucket auto-outsource rule config ─────────────────────────
// Super-admin modal: pick the outside vendor who automatically receives
// every new request in this bucket as an emailed task.
function OutsourceRuleModal({ type, bucketLabel, onClose }) {
  const sourceType = `request.${type}`;
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Items already in process in this bucket (open, not yet outsourced).
  // Turning the rule on covers new + future items; this checkbox sends
  // the in-flight ones too.
  const [openCount, setOpenCount] = useState(0);
  const [applyToOpen, setApplyToOpen] = useState(false);

  useEffect(() => {
    api.getOutsourceRule(sourceType)
      .then(({ rule, open_count }) => {
        if (rule) {
          setActive(Boolean(rule.is_active));
          setName(rule.recipient_name || '');
          setEmail(rule.recipient_email || '');
        }
        setOpenCount(open_count || 0);
        setLoading(false);
      })
      .catch(err => { setError(err.message || 'Failed to load rule'); setLoading(false); });
  }, [sourceType]);

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSaved(false);
    setSubmitting(true);
    try {
      const result = await api.saveOutsourceRule(sourceType, {
        recipient_name: name.trim(),
        recipient_email: email.trim(),
        is_active: active,
        apply_to_open: active && applyToOpen,
      });
      setSaved(result?.applied ? `Saved ✓ — ${result.applied} in-process item${result.applied === 1 ? '' : 's'} sent` : 'Saved ✓');
      setTimeout(onClose, result?.applied ? 1600 : 900);
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSubmitting(false);
  }

  return (
    <Dialog onClose={onClose} busy={submitting} maxWidth={460}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
          📤 Auto-Outsource — {bucketLabel}
        </h2>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.5 }}>
          When this is on, <strong>every new {bucketLabel} request</strong> is automatically emailed
          to the person below with the details and a <strong>Task Complete</strong> button (no login
          needed). The task stays on your team's list until they mark it done.
        </p>
        {loading ? (
          <div style={{ fontSize: 13, color: '#9ca3af', padding: '12px 0' }}>Loading…</div>
        ) : (
          <form onSubmit={handleSave}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 14,
              borderRadius: 8, cursor: 'pointer',
              background: active ? '#fffbeb' : '#f9fafb',
              border: `1px solid ${active ? '#fde68a' : '#e5e7eb'}`,
            }}>
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#b45309' }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: active ? '#b45309' : '#6b7280' }}>
                {active ? 'Auto-outsourcing is ON' : 'Auto-outsourcing is OFF'}
              </span>
            </label>
            <div style={{ marginBottom: 12 }}>
              <label className="sales-label">Vendor Name</label>
              <input className="sales-input" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Line & Shine Striping" style={{ width: '100%' }} required={active} disabled={!active} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="sales-label">Vendor Email</label>
              <input className="sales-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="bids@vendor.com" style={{ width: '100%' }} required={active} disabled={!active} />
            </div>
            {active && openCount > 0 && (
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', marginBottom: 16,
                borderRadius: 8, cursor: 'pointer', background: '#f9fafb', border: '1px solid #e5e7eb',
              }}>
                <input type="checkbox" checked={applyToOpen} onChange={e => setApplyToOpen(e.target.checked)}
                  style={{ width: 16, height: 16, marginTop: 2, accentColor: '#b45309' }} />
                <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                  <strong>Also send the {openCount} {bucketLabel} request{openCount === 1 ? '' : 's'} already in process.</strong>
                  <br />
                  <span style={{ color: '#6b7280' }}>Unchecked, only new requests from now on go to the vendor.</span>
                </span>
              </label>
            )}
            {error && <div style={{ padding: '8px 12px', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {saved && <div style={{ padding: '8px 12px', backgroundColor: '#f0fdf4', color: '#15803d', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{saved}</div>}
            <div className="m-dialog-actions">
              <button type="button" className="btn" onClick={onClose} style={{ fontSize: 13, color: '#6b7280', background: '#f3f4f6' }}>
                Close
              </button>
              <button type="submit" className="btn" disabled={submitting} style={{
                fontSize: 13, fontWeight: 700, color: '#fff', background: '#b45309',
                opacity: submitting ? 0.6 : 1,
              }}>
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
    </Dialog>
  );
}
